import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FaCheckCircle, FaExclamationTriangle, FaLink, FaShieldAlt, FaSpinner, FaTimes } from 'react-icons/fa';
import GiftSetService from '../services/GiftSetService';
import { useFeedback } from '../context/FeedbackContext';

const getDefaultNickname = (uuid) => `NFC ${GiftSetService.formatShortUuid(uuid)}`;

const GiftSetPendingBindModal = ({
  currentUuid,
  giftSetContext,
  onBound,
  theme,
}) => {
  const feedback = useFeedback();
  const [pending, setPending] = useState(null);
  const [nickname, setNickname] = useState('');
  const [isWorking, setIsWorking] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const nextPending = GiftSetService.getPendingBind();
    setPending(nextPending);
    if (nextPending && currentUuid) {
      setNickname(getDefaultNickname(currentUuid));
    }
  }, [currentUuid]);

  const shouldShow = useMemo(() => {
    if (!pending || !currentUuid || isDismissed) return false;
    if (pending.ownerUuid === currentUuid) return false;
    if (pending.addedUuids?.includes(currentUuid)) return false;
    if (giftSetContext?.device?.groupId === pending.groupId) return false;
    return true;
  }, [currentUuid, giftSetContext, isDismissed, pending]);

  const detectedState = useMemo(() => {
    const device = giftSetContext?.device;
    if (!device) {
      return {
        canAdd: true,
        title: 'Secure check needed',
        message: 'Lumirae will verify this NFC exists in the system before it is added.',
      };
    }
    if (device.status === 'DISABLED') {
      return {
        canAdd: false,
        title: 'Disabled NFC',
        message: 'This NFC is disabled and cannot be added to a Gift Set.',
      };
    }
    if (device.groupId && device.groupId !== pending?.groupId) {
      return {
        canAdd: false,
        title: 'Already linked',
        message: 'This NFC belongs to another gift. Remove it there before adding it here.',
      };
    }
    if (device.role === 'OWNER') {
      return {
        canAdd: false,
        title: 'Owner NFC detected',
        message: 'Owner NFCs cannot be added as members.',
      };
    }
    if (device.status && device.status !== 'AVAILABLE') {
      return {
        canAdd: false,
        title: 'Not available',
        message: 'This NFC is not available for pairing.',
      };
    }
    return {
      canAdd: true,
      title: 'Available NFC detected',
      message: 'This NFC can become a member entry for the owner gift.',
    };
  }, [giftSetContext, pending]);

  const handleConfirm = async () => {
    if (!pending || !currentUuid || !detectedState.canAdd) return;
    setIsWorking(true);
    try {
      await GiftSetService.completeAddGiftSetMember({
        memberUuid: currentUuid,
        bindSessionToken: pending.token,
        nickname,
      });
      GiftSetService.markPendingMemberAdded(currentUuid);
      feedback.toast({
        type: 'success',
        title: 'NFC Added',
        message: 'This NFC now opens the owner gift content.',
      });
      onBound?.();
    } catch (error) {
      const message = error.message || 'This NFC could not be added.';
      await feedback.alert({
        type: 'error',
        title: 'Cannot Add NFC',
        message,
      });
      if (String(message).toLowerCase().includes('expired')) {
        GiftSetService.clearPendingBind();
        setPending(null);
      }
    } finally {
      setIsWorking(false);
    }
  };

  const handleFinish = () => {
    GiftSetService.clearPendingBind();
    setPending(null);
    setIsDismissed(true);
  };

  if (!shouldShow) return null;

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 z-[6100] flex items-center justify-center bg-[#211B1D]/60 p-5 backdrop-blur-md"
        onMouseDown={(event) => event.stopPropagation()}
        onTouchStart={(event) => event.stopPropagation()}
      >
        <motion.div
          initial={{ opacity: 0, y: 22, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 18, scale: 0.96 }}
          className="lumirae-ds-panel w-full max-w-sm p-6 shadow-2xl"
          style={{
            '--lumirae-accent-primary': theme.accent,
            '--lumirae-accent-soft': theme.accentSoft,
            '--lumirae-surface-raised': theme.cardBg,
            '--lumirae-border-default': theme.cardBorder,
            '--lumirae-text-primary': theme.text,
            '--lumirae-text-secondary': theme.secondaryText,
          }}
        >
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[1.25rem]" style={{ backgroundColor: theme.accentSoft, color: theme.accent }}>
              <FaLink />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[9px] font-black uppercase tracking-[0.24em]" style={{ color: theme.accent }}>
                Gift Set Pairing
              </p>
              <h3 className="mt-1 text-xl font-black uppercase tracking-tight" style={{ color: theme.text }}>
                NFC Detected
              </h3>
              <p className="mt-2 text-sm font-bold leading-relaxed" style={{ color: theme.secondaryText || theme.text }}>
                This NFC will open {pending.groupName || 'the owner gift'} content after it is added.
              </p>
            </div>
            <button type="button" onClick={() => setIsDismissed(true)} className="lumirae-ds-icon-button h-9 w-9" aria-label="Skip this NFC">
              <FaTimes />
            </button>
          </div>

          <div
            className="mt-5 rounded-[1.05rem] border p-4"
            style={{
              borderColor: detectedState.canAdd ? theme.accent : theme.stateError || theme.cardBorder,
              backgroundColor: detectedState.canAdd ? theme.accentSoft : 'rgba(254, 242, 242, 0.82)',
            }}
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5" style={{ color: detectedState.canAdd ? theme.accent : theme.stateError || '#dc2626' }}>
                {detectedState.canAdd ? <FaShieldAlt /> : <FaExclamationTriangle />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[9px] font-black uppercase tracking-[0.2em]" style={{ color: detectedState.canAdd ? theme.accent : theme.stateError || '#dc2626' }}>
                  {detectedState.title}
                </p>
                <p className="mt-1 text-xs font-bold leading-relaxed" style={{ color: theme.text }}>
                  {detectedState.message}
                </p>
                <p className="mt-2 truncate text-[10px] font-black uppercase tracking-widest opacity-50">
                  {GiftSetService.formatShortUuid(currentUuid)}
                </p>
              </div>
            </div>
          </div>

          <label className="mt-5 block">
            <span className="mb-2 block text-[9px] font-black uppercase tracking-widest opacity-50">
              Member name
            </span>
            <input
              value={nickname}
              onChange={(event) => setNickname(event.target.value)}
              disabled={!detectedState.canAdd}
              maxLength={60}
              className="lumirae-ds-input w-full p-4 text-sm font-black"
              placeholder="Table card, candle jar, bridesmaid gift"
            />
          </label>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <button type="button" onClick={handleFinish} className="lumirae-ds-button lumirae-ds-button-secondary px-4 py-4 text-[10px]">
              Finish
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isWorking || !detectedState.canAdd}
              className="lumirae-ds-button lumirae-ds-button-primary px-4 py-4 text-[10px] disabled:opacity-60"
            >
              {isWorking ? <><FaSpinner className="mr-2 inline animate-spin" /> Adding</> : <><FaCheckCircle className="mr-2 inline" /> Add</>}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default GiftSetPendingBindModal;
